import { useEffect, useMemo, useRef, useState } from "react";
import { fetchAgendaWeather } from "../../../services/scheduleWeather";
import {
  indexCityWeatherEntries,
  normalizeWeatherCityKey,
} from "../agendaWeatherCityLookup";
import { normalizeWeatherHourKey } from "../agendaWeatherHourKey";
import {
  AGENDA_WEATHER_POLL_MS,
  isAgendaWeatherBackgroundRefresh,
  shouldRefreshAgendaWeather,
} from "../agendaWeatherRefresh";
import type {
  AgendaWeatherLocation,
  AgendaWeatherPayload,
  CityWeather,
} from "../agendaWeatherTypes";

type UseAgendaWeatherParams = {
  enabled: boolean;
  date: string;
};

export type UseAgendaWeatherResult = {
  data: AgendaWeatherPayload | null;
  loading: boolean;
  error: boolean;
  location: AgendaWeatherLocation | null;
  primaryCityWeather: CityWeather | null;
  cityByName: Map<string, CityWeather>;
  hourRainByCity: Map<string, Map<string, number>>;
  scheduleIdsAtRisk: Set<number>;
};

export function useAgendaWeather({
  enabled,
  date,
}: UseAgendaWeatherParams): UseAgendaWeatherResult {
  const [data, setData] = useState<AgendaWeatherPayload | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [refreshNonce, setRefreshNonce] = useState(0);
  const loadedDateRef = useRef<string | null>(null);
  const lastFetchedAtRef = useRef<number | null>(null);

  useEffect(() => {
    // "focus" and "visibilitychange" fire together when returning to the tab;
    // the staleness check turns them into at most one request.
    const refreshWhenVisible = () => {
      if (
        document.visibilityState === "visible" &&
        shouldRefreshAgendaWeather(lastFetchedAtRef.current, Date.now())
      ) {
        lastFetchedAtRef.current = Date.now();
        setRefreshNonce((value) => value + 1);
      }
    };

    window.addEventListener("focus", refreshWhenVisible);
    document.addEventListener("visibilitychange", refreshWhenVisible);
    const interval = window.setInterval(refreshWhenVisible, AGENDA_WEATHER_POLL_MS);

    return () => {
      window.removeEventListener("focus", refreshWhenVisible);
      document.removeEventListener("visibilitychange", refreshWhenVisible);
      window.clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    if (!enabled) {
      loadedDateRef.current = null;
      setData(null);
      setLoading(false);
      setError(false);
      return;
    }

    const controller = new AbortController();
    let cancelled = false;
    const isBackground = isAgendaWeatherBackgroundRefresh(loadedDateRef.current, date);

    (async () => {
      // Another day needs a fresh forecast; the same day keeps the old one on screen.
      if (!isBackground) {
        setData(null);
        setLoading(true);
        setError(false);
      }
      lastFetchedAtRef.current = Date.now();

      try {
        const payload = await fetchAgendaWeather({ date }, controller.signal);
        if (!cancelled) {
          loadedDateRef.current = date;
          setData(payload);
          setError(false);
        }
      } catch (err: unknown) {
        if (cancelled) {
          return;
        }
        if ((err as { code?: string }).code === "ERR_CANCELED") {
          return;
        }
        // A failed background refresh keeps the forecast already shown.
        if (!isBackground) {
          setError(true);
          setData(null);
        }
      } finally {
        if (!cancelled && !isBackground) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [enabled, date, refreshNonce]);

  const cityByName = useMemo(() => {
    const map = indexCityWeatherEntries(data?.cities ?? []);
    const resolvedCity = data?.location?.city?.trim();
    if (resolvedCity) {
      const match = data?.cities.find(
        (entry) =>
          normalizeWeatherCityKey(entry.cityName) ===
          normalizeWeatherCityKey(resolvedCity)
      );
      if (match) {
        map.set(resolvedCity, match);
      }
    }
    return map;
  }, [data?.cities, data?.location?.city]);

  const primaryCityWeather = useMemo(() => {
    if (!data?.cities.length) {
      return null;
    }
    const resolvedCity = data.location?.city?.trim();
    if (resolvedCity) {
      const match = data.cities.find(
        (entry) =>
          normalizeWeatherCityKey(entry.cityName) ===
          normalizeWeatherCityKey(resolvedCity)
      );
      if (match) {
        return match;
      }
    }
    return data.cities[0] ?? null;
  }, [data]);

  const hourRainByCity = useMemo(() => {
    const map = new Map<string, Map<string, number>>();
    data?.cities.forEach((cityWeather) => {
      const hours = new Map<string, number>();
      cityWeather.hours.forEach((hour) => {
        const key = normalizeWeatherHourKey(hour.time);
        hours.set(key, hour.rainChance);
      });

      const labels = new Set<string>([
        cityWeather.cityName,
        normalizeWeatherCityKey(cityWeather.cityName),
      ]);
      labels.forEach((label) => {
        if (label) {
          map.set(label, hours);
        }
      });
    });
    return map;
  }, [data]);

  const scheduleIdsAtRisk = useMemo(() => {
    const ids = new Set<number>();
    data?.alerts.forEach((alert) => {
      alert.scheduleIds.forEach((id) => ids.add(id));
    });
    return ids;
  }, [data]);

  return {
    data,
    loading,
    error,
    location: data?.location ?? null,
    primaryCityWeather,
    cityByName,
    hourRainByCity,
    scheduleIdsAtRisk,
  };
}

export { findHourlyRainForSlot } from "../agendaWeatherHour";
